import React from 'react';

const POLL_INTERVAL_MS = 60_000;

/* Polls /version.json and reports whether the deployed build differs from the build
 * compiled into the running bundle. The CI build embeds a build id via Vite's `define`
 * (`__BUILD_ID__`) and emits the same id to dist/version.json; when a new deploy lands,
 * the served file changes while the running tab keeps the old id — that's our signal. */
export function useVersionCheck(): { updateAvailable: boolean } {
  const [updateAvailable, setUpdateAvailable] = React.useState(false);

  React.useEffect(() => {
    /* Skip in dev — the placeholder id is "dev" on both sides and there's no point
     * hammering the dev server for a banner that should never trigger. */
    if (__BUILD_ID__ === 'dev') return;

    let cancelled = false;
    let done = false;
    let intervalId: number | undefined;

    const stopPolling = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const check = async () => {
      if (done) return;
      try {
        /* `cache: 'no-store'` skips the HTTP cache; the query string defeats CDNs that
         * ignore cache-control on static assets. */
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as { id?: string };
        if (cancelled || done) return;
        if (data.id && data.id !== __BUILD_ID__) {
          done = true;
          stopPolling();
          setUpdateAvailable(true);
        }
      } catch {
        /* Network errors are swallowed; the next interval tick will try again. */
      }
    };

    const onFocus = () => {
      void check();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check();
    };

    void check();
    intervalId = window.setInterval(check, POLL_INTERVAL_MS);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stopPolling();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { updateAvailable };
}
